import * as React from 'react';
import { RouteComponentProps } from 'react-router';

import { Card } from '@blueprintjs/core';
import ProjectType from 'src/models/ProjectType';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ODataOptions } from 'src/util/ODataBuilder';
import CardContainer from 'src/components/common/CardContainer';
import Client from 'src/models/Client';
import { SelectItem } from 'src/models/common/SelectItem';
import { Form, AnyObject } from 'react-final-form';
import State from 'src/models/State';
import PropertyUtil from 'src/util/PropertyUtil';
import arrayMutators from 'final-form-arrays';
import ProjectModel from 'src/models/Project';

import Textbox from 'src/components/controls/forms/redesign/FormTextbox';
import Select from 'src/components/controls/forms/redesign/FormFilterSelect';
import Button from '../controls/forms/redesign/FormButton';
import Office from 'src/models/Office';
import County from 'src/models/County';
import Project from 'src/models/Project';
import { connect } from 'react-redux';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import actions from 'src/actions';
import TagContainer from '../counties/TagContainer';
import User from 'src/models/User';

// import FormCheckbox from '../controls/forms/redesign/FormCheckbox';

export interface IEditProjectPageStateProps {
    // client: (id: number) => Client;
    project: (clientNumber: number, projectNumber: number) => Project;
    isProjectTypesLoading: boolean;
    projectTypes: ProjectType[];
    states: PagedResultSet<State>;
    stateCounties: PagedResultSet<County>;
    offices: Office[];
    pcapmList: PagedResultSet<User>;
}

export interface IEditProjectPageDispatchProps {
    getProject: (clientNumber: number, projectNumber: number) => void;
    getProjectTypes: (options: ODataOptions) => void;
    getStates: (options: ODataOptions) => void;
    getOffices: () => void;
    getStateCounties: (stateId: number, options: ODataOptions) => void;
    getProjectCounties: (clientNumber: number, projectNumber: number, options: ODataOptions) => void;
    getPCAPMList: () => void;
    updateProject: (project: ProjectModel) => void;
    initializeProjects: () => void;
}

export type IEditProjectPageProps = RouteComponentProps<{ clientNumber?: string; projectNumber?: string }> &
    IEditProjectPageStateProps &
    IEditProjectPageDispatchProps;

class EditProjectPage extends React.Component<IEditProjectPageProps> {
    private clientNumber: number;
    private projectNumber: number;

    public componentDidUpdate(prevProps: IEditProjectPageProps) {
        if (
            this.props.project(this.clientNumber, this.projectNumber) &&
            this.props.project(this.clientNumber, this.projectNumber) !== prevProps.project(this.clientNumber, this.projectNumber)
        ) {
            this.props.getStateCounties(this.props.project(this.clientNumber, this.projectNumber).state.id, new ODataOptions());
        }
    }

    public componentDidMount() {
        this.props.initializeProjects();
        this.clientNumber = parseInt(this.props.match.params.clientNumber, 10);
        this.projectNumber = parseInt(this.props.match.params.projectNumber, 10);
        this.props.getProject(this.clientNumber, this.projectNumber);
        this.props.getProjectTypes(new ODataOptions());
        this.props.getStates(new ODataOptions().addPaging(1, 10000));
        this.props.getOffices();
        this.props.getPCAPMList();
    }

    handleSubmit = (project: ProjectModel) => {
        project.client = project.client ? project.client : new Client();
        project.client.id = this.clientNumber;
        this.props.updateProject(project);
    };

    stateSelected = (id: number) => {
        this.props.getStateCounties(id, new ODataOptions());
    };

    validate = (project: AnyObject) => {
        const errors: AnyObject = {};

        if (!project.name) {
            errors.name = 'Name is Required';
        }

        // if (!project.projectType || !project.projectType.id) {
        //     errors.projectType = {};
        //     errors.projectType.id = 'Project type is required';
        // }

        if (!project.landSystemId) {
            errors.landSystemId = 'Land System is required';
        }

        if (!project.state || !project.state.id) {
            errors.state = {};
            errors.state.id = 'State is required';
        }

        if (!project.managingOfficeId) {
            errors.managingOfficeId = 'Office is required';
        }

        return errors;
    };

    render() {
        const project = this.props.project(this.clientNumber, this.projectNumber);
        const projectTypes = PropertyUtil.SafeGet(() => this.props.projectTypes);
        const selectProjectTypes = projectTypes ? projectTypes.map((projectType: ProjectType) => new SelectItem(projectType.id, projectType.name)) : undefined;
        const selectLandSystems = [
            new SelectItem(1, 'Jeffersonian'),
            new SelectItem(3, 'West Texas'),
            new SelectItem(4, 'East Texas'),
            new SelectItem(2, 'Eastern'),
        ];

        const states = PropertyUtil.SafeGet(() => this.props.states.items);
        const selectStates = states ? states.map((state: State) => new SelectItem(state.id, state.name)) : undefined;

        const offices = PropertyUtil.SafeGet(() => this.props.offices);
        const managingOfficesList = offices ? offices.map((office: Office) => new SelectItem(office.id, office.name)) : undefined;

        const stateCounties = PropertyUtil.SafeGet(() => this.props.stateCounties.items);
        const selectStateCounties = stateCounties ? stateCounties.map((c: County) => new SelectItem(c.id, c.name)) : undefined;

        const pcapms = PropertyUtil.SafeGet(() => this.props.pcapmList.items);
        const selectPcapms = pcapms ? pcapms.map((p: User) => new SelectItem(p.id, `${p.firstName} ${p.lastName}`)) : undefined;

        return (
            <CardContainer>
                <Card elevation={1}>
                    <h3>Edit Project {project && project.name}</h3>
                    <Form
                        onSubmit={this.handleSubmit}
                        mutators={{ ...arrayMutators }}
                        validate={this.validate}
                        initialValues={{
                            ...project,
                            county: selectStateCounties && selectStateCounties.length === 1 ? selectStateCounties[0].id : undefined,
                        }}
                        render={({ handleSubmit, values, form }) => {
                            // const isShadow = values.isShadow;

                            return (
                                <form onSubmit={handleSubmit}>
                                    <Textbox label="Project Name" name="name" />
                                    {/* <FormCheckbox label="Shadow" name="isShadow" />
                                    {isShadow && (
                                        <>
                                            <Textbox label="Shadow Project Name" name="cloakName" />
                                            <Textbox label="Shadow Client Name" name="client.cloakName" />
                                        </>
                                    )} */}

                                    <Select label="Land System" name="landSystemId" items={selectLandSystems} defaultOption="Select Land System" />
                                    <Select label="Project Type" name="projectType.id" items={selectProjectTypes} defaultOption="Select Project Type" />
                                    <Select
                                        label="State"
                                        name="state.id"
                                        items={selectStates}
                                        defaultOption="Select State"
                                        onChange={this.stateSelected}
                                    />
                                    <TagContainer
                                        label="Counties"
                                        isTagCreatable={false}
                                        fieldArrayName="counties"
                                        fieldAddArrayName="addCounties"
                                        fieldDeleteArrayName="deleteCounties"
                                        initialValueName="county"
                                        form={form}
                                        selectTags={selectStateCounties ? selectStateCounties : new Array<SelectItem>()}
                                    />
                                    <Select label="Managing Office" name="managingOfficeId" items={managingOfficesList} />
                                    <Select label="PCAPM" name="pcapmId" items={selectPcapms} />
                                    <br />
                                    <div style={{ marginTop: '1rem' }}>
                                        <Button type="submit" text="Save Project" />
                                    </div>
                                </form>
                            );
                        }}
                    />
                </Card>
            </CardContainer>
        );
    }
}

const mapStateToProps = (state: IRootState): IEditProjectPageStateProps => ({
    project: (clientNumber: number, projectNumber: number) => selectors.project.project(clientNumber, projectNumber)(state),
    isProjectTypesLoading: selectors.project.isLoading(state),
    projectTypes: selectors.project.projectTypes(state),
    states: selectors.state.states(state),
    offices: selectors.office.offices(state),
    pcapmList: selectors.user.users(state),
    stateCounties: selectors.county.stateCounties(state),
});

const mapDispatchToProps: IEditProjectPageDispatchProps = {
    getProject: (clientNumber: number, projectNumber: number) => actions.project.getProject(clientNumber, projectNumber),
    getProjectTypes: (options: ODataOptions) => actions.project.getProjectTypes(options),
    getStates: (options: ODataOptions) => actions.state.getStates(options),
    getOffices: () => actions.office.getOffices(),
    updateProject: (project: ProjectModel) => actions.project.updateProject(project),
    getStateCounties: (stateId: number, options: ODataOptions) => actions.county.getStateCounties(stateId, options),
    getProjectCounties: (clientNumber: number, projectNumber, options: ODataOptions) =>
        actions.county.getProjectCounties(clientNumber, projectNumber, options),
    getPCAPMList: () => actions.user.getUsers(),
    initializeProjects: () => actions.project.initializeProjects(),
};

export default connect(mapStateToProps, mapDispatchToProps)(EditProjectPage);



// WEBPACK FOOTER //
// ./src/components/project/EditProjectPage.tsx